import { Component } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import _ from 'underscore';
import type { ReactNode } from 'react';

import { window } from '../../globals';
import { getAuthorizeServiceURL } from '../../helpers/services';
import { get } from '../../redux/api';
import utils from '../../utils';
import { ErrorAlert, IconText } from '../generic';
import Button, { ButtonGroup } from '../generic/Button';
import Flex from '../generic/Flex';
import { LabeledMultiTextInput } from '../generic/Forms';
import Icon, { icons } from '../generic/Icon';
import Link from '../generic/Link';
import Loader from '../generic/Loader';
import Table from '../generic/Table';
import Text from '../generic/Text';
import type { AsyncStatus } from '../../types';

type IModalInviteUsersProps = {
  show?: boolean;
  onCreateUserInvitations: (emails: Set<string>) => Promise<void>;
  createInviteIsLoading: boolean;
  createInviteError?: string;
  onHide: () => void;
};

type IModalInviteUsersState = {
  invite_email: string;
  loading?: boolean;
  error: string | null;
  success: boolean;
  org_members?: GitHubOrgs | null;
  selected_org?: string | null;
  hasAuthError: boolean;
};

type GitHubUser = {
  email?: string;
  icon: string;
  login: string;
  // selected is added by this component.
  selected?: boolean;
};

type GitHubOrgs = Record<string, {
  icon: string;
  users: GitHubUser[];
  invalid_users: string[];
}>;

const initialState: IModalInviteUsersState = {
  invite_email: '',
  loading: false,
  error: null,
  success: false,
  org_members: null,
  selected_org: null,
  hasAuthError: false,
};

class ModalInviteUsers extends Component<IModalInviteUsersProps, IModalInviteUsersState> {
  constructor(props: IModalInviteUsersProps) {
    super(props);
    this.state = initialState;
  }

  onInviteSend = async () => {
    let emails: string[];
    if (this.state.selected_org) {
      // if emails from github, send bulk action
      const orgMembers = this.state.org_members?.[this.state.selected_org];
      emails = _.compact(
        (orgMembers?.users || []).map(user => user.selected ? user.email : null),
      );
    } else {
      emails = this.state.invite_email.replace(/\s/g, '').split(',');
    }
    this.setState({ success: false });

    await this.props.onCreateUserInvitations(new Set(emails));
    this.onHide();
  };

  onRenderGithubOrgs = () => {
    this.setState({ loading: true });
    get<GitHubOrgs>('/api/services/github/organizationMembers')
      .then((resp) => {
        if (!resp.data) {
          return;
        }

        const orgMembers: GitHubOrgs = resp.data;
        const orgNames = _.keys(orgMembers);
        orgNames.forEach(org => {
          const noEmail: string[] = [];
          const hasEmail: GitHubUser[] = [];
          (orgMembers[org].users || []).forEach(user => {
            if (!user.email) {
              noEmail.push(user.login);
              return;
            }
            user.selected = true;
            hasEmail.push(user);
          });
          orgMembers[org].users = hasEmail;
          orgMembers[org].invalid_users = noEmail;
        });
        this.setState({
          org_members: orgMembers,
          selected_org: orgNames.length > 0 ? orgNames[0] : null,
          loading: false,
          error: null,
        });
      })
      .catch(err => {
        const isAuthError = err.message.includes('Service needs authentication');
        this.setState({
          loading: false,
          error: !isAuthError ? err.data : null,
          hasAuthError: isAuthError,
        });
      });
  };

  onSelectOrganization = org => {
    this.setState({ selected_org: org });
  };

  onRenderGithubUser = (user: GitHubUser) => ([
    <td key={`login_${user.login}`}>
      {user.icon ? (
        <img
          src={user.icon}
          style={{
            maxHeight: 24,
            maxWidth: 24,
            borderRadius: '2px',
            marginRight: '10px',
          }}
        />
      ) : null}
      {user.login}
    </td>,
    <td key={`email_${user.login}`}>{user.email || 'No email found'}</td>,
  ]);

  onRowClick = (rowData: GitHubUser) => {
    if (!rowData.email) {
      return;
    }
    const new_org_members = this.state.org_members;
    const index = new_org_members[this.state.selected_org].users.findIndex(user => user.login === rowData.login);
    if (index > -1) {
      new_org_members[this.state.selected_org].users[index].selected = !new_org_members[this.state.selected_org]
        .users[index].selected;
      this.setState({ org_members: new_org_members });
    }
  };

  onRowSelect = (index: number) => {
    const new_org_members = this.state.org_members;
    new_org_members[this.state.selected_org].users[index].selected = !new_org_members[this.state.selected_org].users[
      index
    ].selected;
    this.setState({ org_members: new_org_members });
  };

  onSelectAllGithubUsers = utils.withTargetChecked(isChecked => {
    const new_org_members = this.state.org_members;
    _.each(new_org_members[this.state.selected_org].users, (user: any) => {
      if (user.email) {
        user.selected = isChecked;
      }
    });
    this.setState({ org_members: new_org_members });
  });

  onBackClick = () => {
    this.setState({ selected_org: null, org_members: null, error: null });
  };

  onHide = () => {
    this.setState(initialState);
    this.props.onHide();
  };

  render() {
    let modalBody;
    if (this.state.loading) {
      modalBody = <Loader />;
    } else if (this.state.selected_org) {
      const orgUsers = this.state.org_members[this.state.selected_org];
      modalBody = (
        <div>
          <div className="text-center">
            <Text.H2>
              Invite users from your GitHub organization
            </Text.H2>
          </div>
          <div className="padded-small">
            <div className="row">
              <div className="col-xs-3">
                <Filters
                  onSelect={this.onSelectOrganization}
                  org_members={this.state.org_members}
                  selected_org={this.state.selected_org}
                />
              </div>
              <div className="col-xs-9 panel" style={{ overflowY: 'auto', maxHeight: '400px' }}>
                <Table
                  columns={['user', 'email']}
                  className="table-fill"
                  rows={orgUsers.users}
                  renderRow={this.onRenderGithubUser}
                  onRowClick={this.onRowClick}
                  onRowSelect={this.onRowSelect}
                  onAllSelect={this.onSelectAllGithubUsers}
                />
              </div>
            </div>
            {orgUsers.invalid_users.length ? (
              <div className="row" style={{ marginTop: '20px' }}>
                <span>NOTE: Some of the users did not have an email associated with their account.</span>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div>
                        <b>{orgUsers.invalid_users.join(', ')}</b>
                      </div>
                    </Tooltip>
                  }>
                  <a style={{ padding: '7px' }}>View here.</a>
                </OverlayTrigger>
              </div>
            ) : null}
          </div>
        </div>
      );
    } else {
      modalBody = (
        <LabeledMultiTextInput
          label="Email Address(es)"
          onChange={(emails: string[]) => {
            this.setState({
              invite_email: emails.join(','), // They are split later before sending.
              success: false,
              error: null,
            });
          }}
          placeholder="Enter an email address and press enter"
          size="large"
          values={this.state.invite_email.split(',').filter(item => !!item.trim())}
        />
      );
    }

    let errorMessage: ReactNode | undefined;
    if (this.state.hasAuthError) {
      errorMessage = (
        <div className="alert alert-error">
          Error connecting to GitHub.
          To connect to GitHub <Link to={getAuthorizeServiceURL({ vcsHost: 'github', location: window.location.href, serviceConnect: true })} openInNewTab>click here.</Link>
        </div>
      );
    } else if (this.state.error || this.props.createInviteError) {
      const combinedStatus = {
        errors: [this.state.error, this.props.createInviteError].filter(Boolean),
      };
      errorMessage = (
        <ErrorAlert status={combinedStatus as AsyncStatus} />
      );
    }

    return (
      <Modal show={this.props.show} onHide={this.onHide} bsSize={this.state.selected_org ? 'large' : null}>
        <Modal.Header>
          <Flex align="center" justify="space-between">
            <Text.H1>
              Invite Users
            </Text.H1>
            {!this.state.selected_org ? (
              <Button
                size="large"
                type="secondary"
                onClick={this.onRenderGithubOrgs}
              >
                <span className="margin-right-sm" style={{ fontSize: '1.25em' }}>
                  <Icon icon={icons.github} />
                </span>
                Invite from Github
              </Button>
            ) : (
              <Button
                size="medium"
                type="secondary"
                onClick={this.onBackClick}
              >
                Go Back
              </Button>
            )}
          </Flex>
        </Modal.Header>
        <Modal.Body>
          {modalBody}
          {errorMessage}
          {this.state.success ? <div className="alert alert-success">Invite successfully sent!</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Button
              type="secondary"
              size="medium"
              onClick={this.onHide}
            >
              Close
            </Button>
            <Button
              type="primary"
              size="medium"
              onClick={this.onInviteSend}
              isDisabled={this.state.loading}
            >
              {this.props.createInviteIsLoading
                ? (
                  <IconText icon={icons.circleO} hasSpin text="Send Invite" />
                  )
                : (
                    'Send Invite'
                  )}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    );
  }
}

class Filters extends Component<any, any> {
  renderItems = org => {
    const curr_org = this.props.org_members[org];
    return (
      <li>
        <a className={this.props.selected_org === org ? 'active' : ''} onClick={this.props.onSelect.bind(null, org)}>
          {curr_org.icon ? (
            <img
              src={curr_org.icon}
              style={{
                maxHeight: 24,
                maxWidth: 24,
                borderRadius: '2px',
                marginRight: '10px',
              }}
            />
          ) : (
            <span className="margin-right-xs">
              <Icon icon={icons.circleThin} />
            </span>
          )}&nbsp;{utils.trimString(org, 18)}
        </a>
      </li>
    );
  };

  render() {
    return (
      <div>
        <div>
          <ul className="nav nav-minimal">
            {_.map(_.keys(this.props.org_members), org => [<li key={org} className="header" />, this.renderItems(org)])}
          </ul>
        </div>
      </div>
    );
  }
}

export default ModalInviteUsers;
